import {dom, DomElementArg, styled} from 'grainjs';
import {GameModel, PlayerModel, Victory} from './game-model';
import {cssLabelStatus, cssWrapper, editableLabel} from './editableLabel';

// Simple form to ask player for their name, and call the given callback with it.
export function buildNameQuestion(playerModel: PlayerModel) {
  let input: HTMLInputElement;
  return buildPage({},
    buildBoard(),
    cssOverlay(
      cssPromptBlock(
        "What's your name?",
        cssNameForm(
          input = cssNameInput({name: 'name', type: 'text'},
            (elem) => { setTimeout(() => elem.focus(), 0); },
          ),
          cssAttachedButton({type: 'submit'}, 'OK'),
          dom.on('submit', (ev, elem) => { ev.preventDefault(); playerModel.name.set(input.value); }),
        ),
      ),
    ),
  );
}

export function buildHome(
  playerModel: PlayerModel,
  createRoom: () => void,
  joinRoom: (code: string) => Promise<void>,
) {
  let roomCodeInput: HTMLInputElement;
  let form: HTMLFormElement;
  function validate(err: Error) {
    roomCodeInput.setCustomValidity(err.message);
    form.reportValidity();
  }
  function maybeJoin() {
    if (roomCodeInput.value && roomCodeInput.checkValidity()) {
      joinRoom(roomCodeInput.value).catch(validate);
    } else {
      roomCodeInput.focus();
    }
  }
  return buildPage({},
    buildBoard(),
    cssOverlay(
      cssWelcome('Welcome, ', cssEditableLabel(playerModel.name, async (n) => playerModel.name.set(n)), "!"),
      cssPromptButton('Invite friend', dom.on('click', () => createRoom())),
      cssPromptButton('Join Game',
        form = dom('form', {style: 'display: inline-block'},
          roomCodeInput = cssInputCode({
              type: 'text', placeholder: 'room code', inputmode: 'numeric',
              size: '6', pattern: '[0-9]{6}'
            },
            dom.on('click', (ev) => ev.stopPropagation()),
            dom.on('input', maybeJoin),
          ),
          dom.on('click', maybeJoin),
        ),
      ),
    )
  );
}

export function buildRoom(playerModel: PlayerModel, game: GameModel) {
  let codeInput: HTMLInputElement;
  return buildPage({homeButton: true},
    buildBoard(game),
    dom.domComputed(game.state, (state) => {
      if (state === 'error') {
        return cssOverlay(
          cssPromptBlock(dom.text(game.errorMessage))
        );
      }
      if (state === 'waiting') {
        return cssOverlay(
          cssPromptCodeBlock('Share code ',
            cssRoomCode(
              codeInput = cssCodeInput({value: game.roomCode, readonly: true, size: '6'}),
              cssCopyPaste('\u29C9'),
              dom.on('click', () => {
                codeInput.select();
                document.execCommand('copy');
              }),
            ),
            ' or the URL.',
          ),
          cssPromptBlock("Waiting for your friend..."),
        );
      }
    }),
    dom.domComputed(game.winner, (winner) =>
      winner === null ? null : cssOverlay(
        cssPromptBlock(winner >= 0 ? `${game.players.get()[winner].name} WON!` : `It's a TIE!`,
          {style: 'margin-top: 45px; width: 250px'}
        ),
      )
    ),
  )
}

interface PageOptions {
  homeButton?: boolean;
}

function buildPage(options: PageOptions, ...args: DomElementArg[]) {
  return cssPage(
    cssHeader(
      options.homeButton ? cssHomeButton('\u2302', {href: '/'}) : null,
      cssTitle('Tic-Tac-Toe'),
    ),
    ...args,
  )
}

function buildBoard(game?: GameModel) {
  return cssGame(
    cssBoard(
      Array.from(Array(3), (unused, row) => cssRow(
        Array.from(Array(3), (unused2, col) => cssTile(game ? dom.text((use) => use(game.board)[row][col]) : null))
      )),
      dom.onMatch(`.${cssTile.className}`, 'click', (ev, elem) => {
        if (game) {
          const rowElem = (elem as Element).parentElement!;
          const boardElem = rowElem.parentElement!;
          const col = Array.prototype.indexOf.call(rowElem.children, elem);
          const row = Array.prototype.indexOf.call(boardElem.children, rowElem);
          game.onClick(row, col);
        }
      }),
      dom.maybe(game && game.victory, (victory) => {
        const canvas = cssVictory();
        setTimeout(() => drawVictoryLine(canvas, victory), 0);
        return canvas;
      }),
    ),
    game ? cssPlayers(
      cssPlayersHeader('Players:'),
      dom.domComputed(game.players, (players) => {
        return players.map((playerInfo, i) =>
          cssPlayer(playerInfo.id === game.playerId ? 'You, ' : '', playerInfo.name,
            cssPlayer.cls('-active', (use) => use(game.whoseTurn) === i),
          ),
        );
      }),
      dom.maybe((use) => use(game.winner) !== null, () =>
        cssNewGame('New Game', dom.on('click', () => game.newGame())),
      )
    ) : null
  );
}

function drawVictoryLine(canvas: HTMLCanvasElement, victory: Victory) {
  // Get the device pixel ratio, falling back to 1. We'll scale the canvas for sharper lines.
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.strokeStyle = 'red';
  const [start, end] = victory;
  const tailY = (start[0] !== end[0]) ? 0.35 : 0;
  const tailX = (start[1] !== end[1]) ? 0.35 : 0;
  ctx.moveTo(
    (start[1] + 0.5 + tailX * (start[1] - 1)) * (rect.width / 3),
    (start[0] + 0.5 + tailY * (start[0] - 1)) * (rect.height / 3)
  );
  ctx.lineTo(
    (end[1] + 0.5 + tailX * (end[1] - 1)) * (rect.width / 3),
    (end[0] + 0.5 + tailY * (end[0] - 1)) * (rect.height / 3)
  );
  ctx.stroke();
}

const green = '#78d578';
const greenHover = '#6bc16b';
const lineColor = '#62b2cc';

const cssPage = styled('div', `
  box-sizing: border-box;
  font-family: sans-serif;
  width: 500px;
  margin: 0px auto;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`);

const cssHeader = styled('div', `
  width: 100%;
`);

const cssHomeButton = styled('a', `
  background-color: ${green};
  color: white;
  font-size: 28px;
  border-radius: 8px;
  margin: 20px;
  width: 40px;
  height: 40px;
  text-align: center;
  text-decoration: none;
  outline: none;
  line-height: 40px;
  border: none;
  position: absolute;
  cursor: pointer;
  &:hover {
    background-color: ${greenHover};
  }
`);

const cssTitle = styled('div', `
  text-align: center;
  color: lightgrey;
  font-size: 32px;
  font-weight: medium;
  margin-top: 20px;
`);

const cssBoard = styled('div', `
  margin: auto;
  width: 300px;
  height: 300px;
  margin: 30px;
  display: flex;
  flex-direction: column;
  position: relative;
`);

const cssRow = styled('div', `
  height: 100px;
  border-top: 2px solid ${lineColor};
  display: flex;
  &:first-child {
    border-top: none;
  }
`);

const cssTile = styled('div', `
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 10px;

  width: 100px;
  border-left: 2px solid ${lineColor};
  font-size: 80px;
  &:first-child {
    border-left: none;
  }
`);

const cssOverlay = styled('div', `
  position: absolute;
  width: 500px;
  top: 100px;
  left: 0px;
`);

const cssWelcome = styled('div', `
  border: 2px solid ${green};
  background-color: white;
  color: grey;
  font-size: 28px;
  border-radius: 8px;
  width: 400px;
  line-height: 48px;
  padding: 10px 20px;
  margin: 20px;
  display: flex;
`);

const cssPromptBlock = styled('div', `
  background-color: ${green};
  color: white;
  font-size: 28px;
  border-radius: 8px;
  width: 400px;
  line-height: 48px;
  padding: 10px 20px;
  margin: 20px;
`);

const cssPromptCodeBlock = styled(cssPromptBlock, `
  font-size: 24px;
  white-space: nowrap;
`);

const cssPromptButton = styled(cssPromptBlock, `
  text-align: center;
  cursor: pointer;
  &:hover {
    background-color: ${greenHover};
  }
`);

const cssNameForm = styled('form', `
  border: 1px solid lightgrey;
  border-radius: 4px;
  display: flex;
  overflow: hidden;
`);

const cssNameInput = styled('input', `
  flex: 1 1 auto;
  border: none;
  padding: 8px 16px;
  font-family: inherit;
  font-size: 24px;
  color: grey;
  outline: none;
  line-height: 32px;
  margin: 0;
`);

const cssEditableLabel = styled(editableLabel, `
  &.${cssWrapper.className} {
    padding-left: 0.25em;
    margin-left: 0.25em;
    border-radius: 8px;
    display: inline-flex;
    overflow: hidden;
  }
  &:hover {
    background-color: #F0F0F0;
  }

  &.${cssLabelStatus.className}-normal::after,
  &.${cssLabelStatus.className}-editing::after {
    color: #A0A0A0;
    border: none;
    cursor: pointer;
    font-size: 20px;
    font-weight: medium;
    background: none;
    width: 32px;
    height: 32px;
    text-align: center;
    border-radius: 4px;
    z-index: 1;
  }
  &.${cssLabelStatus.className}-normal::after {
    content: '\u270E';
    transform: scaleX(-1);
  }
  &.${cssLabelStatus.className}-editing::after {
    content: '\u2713';
  }
`);

const cssAttachedButton = styled('button', `
  padding: 8px 16px;
  border: none;
  border-left: 1px solid lightgrey;
  cursor: pointer;
  font-size: inherit;
  font-weight: inherit;
  margin: 0;
  background-color: #F0F0F0;
  &:hover {
    background-color: #E0E0E0;
  }
`);

const cssInputCode = styled('input', `
  margin-left: 20px;
  font-size: 30px;
  border: none;
  border-radius: 4px;
  padding: 5px 20px;
  font-family: monospace;
  &::placeholder {
    font-size: 20px;
  }
`);

const cssRoomCode = styled('div', `
  display: inline-block;
  background-color: white;
  color: black;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #F0F0F0;
  }
`);

const cssCodeInput = styled('input', `
  border: none;
  font-family: monospace;
  font-size: 18px;
  padding: 0px 10px;
  cursor: pointer;
  background-color: inherit;
  outline: none;
`);

const cssCopyPaste = styled('div', `
  display: inline-block;
  border-left: 1px solid lightgrey;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 0px 5px;
  font-weight: bold;
`);

const cssGame = styled('div', `
  display: flex;
`);

const cssPlayers = styled('div', `
  display: flex;
  flex-direction: column;
  margin: 30px;
  font-size: 24px;
  white-space: nowrap;
`);

const cssPlayersHeader = styled('div', `
  text-align: center;
  color: lightgrey;
  font-weight: medium;
`);

const cssPlayer = styled('div', `
  padding: 10px;
  margin: 10px;
  border-radius: 8px;
  height: 24px;
  border: 2px solid ${green};
  &-active {
    background-color: ${green};
    color: white;
  }
`);

const cssNewGame = styled('div', `
  padding: 10px;
  margin: 75px 10px 10px 10px;
  border-radius: 8px;
  height: 24px;
  border: 2px solid ${green};
  background-color: ${green};
  color: white;
  cursor: pointer;
  &:hover {
    background-color: ${greenHover};
    border-color: ${greenHover};
  }
`);

const cssVictory = styled('canvas', `
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
`);
